import { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import type { Session } from '@supabase/supabase-js'


// Phase 8.2: Adaptive Logs UI
// Read-only view of immutable adaptive events

const API_BASE = import.meta.env.VITE_RAG_API_URL

interface AdaptiveEvent {
    id: string
    tenant_id: string
    request_id: string | null
    event_type: 'intent_adjustment' | 'fallback_applied' | 'threshold_suggestion' | string
    intent: string | null
    policy: string | null
    semantic_before: number | null
    semantic_after: number | null
    cosine_before: number | null
    cosine_after: number | null
    fallback_applied: boolean
    metadata: any
    created_at: string
}

interface AdaptiveLogsPanelProps {
    session: Session | null
}

export function AdaptiveLogsPanel({ session }: AdaptiveLogsPanelProps) {
    const [events, setEvents] = useState<AdaptiveEvent[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [accessDenied, setAccessDenied] = useState(false) // 403 specific

    const isMounted = useRef(true)
    const abortControllerRef = useRef<AbortController | null>(null)

    useEffect(() => {
        isMounted.current = true
        if (session?.access_token) {
            fetchEvents()
        }
        return () => {
            isMounted.current = false
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [session])

    const fetchEvents = async () => {
        if (!session?.access_token) return
        if (!API_BASE) {
            setError('System Error: VITE_RAG_API_URL not defined')
            return
        }

        // Cancel previous request if exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
        }
        const controller = new AbortController()
        abortControllerRef.current = controller

        try {
            setLoading(true)
            setError(null)

            const config = {
                headers: { Authorization: `Bearer ${session.access_token}` },
                timeout: 10000,
                signal: controller.signal
            }

            // DIAGNOSTIC: Phase 7.1 Emergency Audit - Removed to prevent sensitive data leak
            const requestUrl = `${API_BASE}/ops/retrieval/adaptive/events?limit=50`

            // Fetch events (default limit=50)
            const res = await axios.get(requestUrl, config)

            if (isMounted.current) {
                setEvents(res.data.events || [])
                // Warning logic from API (migration pending)
                if (res.data.warning) {
                    setError(res.data.warning)
                }
            }

        } catch (err: any) {
            if (axios.isCancel(err)) return // Ignore aborts

            console.error('[AdaptiveLogs] Failed to fetch events:', err)

            if (isMounted.current) {
                if (err.response?.status === 403) {
                    setAccessDenied(true)
                } else if (err.code === 'ECONNABORTED') {
                    setError('Network Timeout (Backend slow or unreachable)')
                } else {
                    setError(err.message || 'Failed to load logs')
                }
            }
        } finally {
            if (isMounted.current) {
                setLoading(false)
            }
        }
    }

    if (accessDenied) {
        // UI Requirement: Hide or Minimal Message for 403
        // Decision: Show minimal "Ops Access Required" placeholder
        return (
            <div className="bg-white/5 border border-white/10 rounded-lg p-6 opacity-60">
                <h3 className="text-xl font-semibold text-white mb-2">Adaptive Logs</h3>
                <p className="text-gray-500 text-sm">🔒 Ops access required to view audit logs.</p>
            </div>
        )
    }

    return (
        <section className="bg-white/5 border border-white/10 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="text-xl font-semibold text-white">Adaptive Audit Logs</h3>
                    <p className="text-gray-400 text-sm">Real-time log of runtime adaptive decisions (50 most recent)</p>
                </div>
                <button
                    onClick={fetchEvents}
                    disabled={loading}
                    className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50"
                >
                    {loading ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            {error ? (
                <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-red-400 text-sm">
                    ⚠️ {error}
                </div>
            ) : (
                <>
                    {events.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 border border-dashed border-gray-700 rounded-lg">
                            No adaptive events recorded yet (System is running within base parameters)
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="border-b border-white/10 text-gray-400">
                                    <tr>
                                        <th className="pb-2 font-medium">Time (UTC)</th>
                                        <th className="pb-2 font-medium">Event Type</th>
                                        <th className="pb-2 font-medium">Intent</th>
                                        <th className="pb-2 font-medium text-right">Semantic Δ</th>
                                        <th className="pb-2 font-medium text-right">Cosine Δ</th>
                                        <th className="pb-2 font-medium text-center">Fallback</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {events.map((evt) => (
                                        <tr key={evt.id} className="hover:bg-white/5 transition-colors">
                                            <td className="py-3 text-gray-300 whitespace-nowrap font-mono text-xs">
                                                {new Date(evt.created_at).toLocaleString()}
                                            </td>
                                            <td className="py-3">
                                                <EventTypeBadge type={evt.event_type} />
                                            </td>
                                            <td className="py-3 text-gray-300">
                                                {evt.intent || '-'}
                                                {evt.policy && <span className="text-xs text-gray-500 ml-1">({evt.policy})</span>}
                                            </td>
                                            <td className="py-3 text-right">
                                                <DeltaValue before={evt.semantic_before} after={evt.semantic_after} />
                                            </td>
                                            <td className="py-3 text-right">
                                                <DeltaValue before={evt.cosine_before} after={evt.cosine_after} />
                                            </td>
                                            <td className={`py-3 text-center ${evt.fallback_applied ? 'text-yellow-500 font-bold' : 'text-gray-600'}`}>
                                                {evt.fallback_applied ? '✓' : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </section>
    )
}

function EventTypeBadge({ type }: { type: string }) {
    const styles: Record<string, string> = {
        intent_adjustment: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        fallback_applied: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        threshold_suggestion: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    }

    // Normalize format (intent_adjustment -> Intent Adjustment)
    const label = type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

    return (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${styles[type] || 'bg-gray-500/20 text-gray-400'}`}>
            {label}
        </span>
    )
}

function DeltaValue({ before, after }: { before: number | null, after: number | null }) {
    if (before === null || after === null) return <span className="text-gray-600">-</span>

    // Check if truly different (float precision)
    const diff = after - before
    if (Math.abs(diff) < 0.001) return <span className="text-gray-500">{after?.toFixed(2)}</span>

    const color = diff > 0 ? 'text-green-400' : 'text-red-400'
    const arrow = diff > 0 ? '↑' : '↓'

    return (
        <div className="flex flex-col items-end leading-tight">
            <span className="text-white">{after?.toFixed(2)}</span>
            <span className={`text-[10px] ${color}`}>
                {before?.toFixed(2)} {arrow}
            </span>
        </div>
    )
}
